/*
 * pcard-variant-select.js
 *
 * v2 product card only. Turns the colour swatches into an in-card variant
 * picker that feeds the card's add-to-cart form, instead of navigating to the
 * product page. Clicking a swatch rewrites the sibling form's hidden
 * `.product-variant-id` value to the swatch's variant, so the selected colour
 * is what gets added to cart. With no selection (or no JS) the form keeps its
 * default first-available variant.
 *
 * Scope is hard-limited to `.product-card--v2` so v1 cards keep their original
 * link-navigation behaviour. If a card has no add-to-cart form (the
 * `pcard_show_info_atc` setting is off), the swatch link is left to navigate
 * normally.
 *
 * A single delegated document listener handles every card on the page; the
 * init guard keeps duplicate script tags (one per card) from binding twice.
 */
(function () {
  if (window.__pcardVariantSelectInit) return;
  window.__pcardVariantSelectInit = true;

  function getVariantId(href) {
    try {
      return new URL(href, window.location.origin).searchParams.get('variant');
    } catch (error) {
      return null;
    }
  }

  // Toggle a card action button between its default label and a disabled
  // "Not for sale" state. Works for an <a> ("Choose options") or a <button>
  // (add-to-cart); native `disabled` is also set on real buttons so the form
  // can't submit. Labels come from the button's data-pcard-atc-* attributes.
  function setCardAtcState(btn, textSelector, notForSale) {
    if (!btn) return;
    var textEl = btn.querySelector(textSelector);
    var defaultLabel = btn.getAttribute('data-pcard-atc-default');
    var nfsLabel = btn.getAttribute('data-pcard-atc-notforsale');
    var classes = ['is-pcard-notforsale', 'tw-opacity-40', 'tw-pointer-events-none', 'tw-cursor-not-allowed'];
    var i;
    // Keep the accessible name in step with the visible label (WCAG 2.5.3 / F96).
    // Only rewrite an aria-label that already exists, so buttons that name
    // themselves purely from their visible text keep doing so.
    if (notForSale) {
      if (textEl && nfsLabel) textEl.textContent = nfsLabel;
      if (nfsLabel && btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', nfsLabel);
      for (i = 0; i < classes.length; i++) btn.classList.add(classes[i]);
      btn.setAttribute('aria-disabled', 'true');
      if (btn.tagName === 'BUTTON') btn.disabled = true;
    } else {
      if (textEl && defaultLabel) textEl.textContent = defaultLabel;
      if (defaultLabel && btn.hasAttribute('aria-label')) btn.setAttribute('aria-label', defaultLabel);
      for (i = 0; i < classes.length; i++) btn.classList.remove(classes[i]);
      btn.removeAttribute('aria-disabled');
      if (btn.tagName === 'BUTTON') btn.disabled = false;
    }
  }

  document.addEventListener('click', function (event) {
    var swatch = event.target.closest('.product-card--v2 .swatch-color[href]');
    if (!swatch) return;

    var card = swatch.closest('.product-card--v2');
    if (!card) return;

    var variantId = getVariantId(swatch.getAttribute('href'));
    if (!variantId) return; // not a variant swatch (e.g. the "+N" link) — let it navigate

    // Selecting a colour is an in-card action, not navigation.
    event.preventDefault();

    // Feed the selected variant to the in-card add-to-cart form, if present.
    var input = card.querySelector('.product-variant-id');
    if (input) {
      input.value = variantId;
      if (input.disabled) input.disabled = false;
    }

    // LAC-A11Y-016: keep programmatic selection state in step with the visual ring.
    // LAC-A11Y-014: preserve every link's no-JS tab stop; only pin the selection.
    var swatches = card.querySelectorAll('.swatch-color[href]');
    for (var i = 0; i < swatches.length; i++) {
      var selected = swatches[i] === swatch;
      swatches[i].classList.toggle('is-pcard-selected', selected);
      swatches[i].setAttribute('aria-checked', selected ? 'true' : 'false');
      if (selected) swatches[i].setAttribute('tabindex', '0');
      else swatches[i].removeAttribute('tabindex');
    }

    // Swap the card's main image to the selected variant's image (when it has one).
    var li = swatch.closest('li');
    var variantImage = li && li.getAttribute('data-pcard-variant-image');
    var variantImageAlt = (li && li.getAttribute('data-pcard-variant-image-alt')) || '';
    if (variantImage) {
      setImg(card.querySelector('.product-card__image--main img'), variantImage, variantImageAlt);
    }

    // Hover image (A+C rule), guarded on its own: a variant with no main image
    // can still have a dedicated hover image.
    //  A — the variant has a dedicated hover image → hover shows it.
    //  C — it doesn't → hover shows the SAME variant image (no mismatched swap).
    var hoverImage = (li && li.getAttribute('data-pcard-variant-image2')) || variantImage;
    if (hoverImage) {
      var hoverImageAlt = li.hasAttribute('data-pcard-variant-image2-alt')
        ? li.getAttribute('data-pcard-variant-image2-alt')
        : variantImageAlt;
      setImg(card.querySelector('.product-card__image--second img'), hoverImage, hoverImageAlt);
    }

    // Update the card's displayed price to the selected variant's price.
    // For a $0 "in store only" variant, hide the price but keep a same-height,
    // invisible placeholder so the price line never collapses — cards stay aligned
    // row-to-row on the grid. The action button carries the "In store only" text.
    var priceWrap = card.querySelector('[data-pcard-price]');
    if (priceWrap && li) {
      var vPrice = li.getAttribute('data-pcard-variant-price');
      var vInStore = li.getAttribute('data-pcard-variant-instore') === 'true';
      // A not-purchasable card (tagged not-for-sale / in-store-only, vendor
      // "Not For Sale", or $0) shows no price — never a variant price — even
      // after a swatch selection; the action button reads "In store only".
      var notForSale = priceWrap.getAttribute('data-pcard-not-purchasable') === 'true';
      var nfs = notForSale || vInStore;
      var priceHidden = '<span class="f-price-item f-price-item--regular" style="visibility:hidden">&nbsp;</span>';
      var fprice = priceWrap.querySelector('.f-price');
      if (nfs) {
        // Reserve the price line's height with an invisible placeholder, not an empty node.
        if (fprice) fprice.innerHTML = priceHidden;
        else priceWrap.innerHTML = '<div class="f-price f-price--left" aria-hidden="true">' + priceHidden + '</div>';
      } else if (fprice) {
        fprice.innerHTML = '<span class="f-price-item f-price-item--regular">' + vPrice + '</span>';
      } else {
        priceWrap.innerHTML = '<div class="f-price f-price--left"><span class="f-price-item f-price-item--regular">' + vPrice + '</span></div>';
      }

      // Mirror the in-store / not-for-sale state on the card's action button(s):
      // a $0 ("In store only") variant — or a fully not-for-sale product — can't
      // be bought online, so the action reads "In store only" and is disabled; a
      // priced variant restores the default label. Covers the overlay action and
      // the optional info-column add-to-cart.
      // Coming-soon is product-level (not per variant), so keep the action locked
      // regardless of the selected swatch — its data-pcard-atc-notforsale reads "Coming soon".
      var comingSoon = card.getAttribute('data-pcard-coming-soon') === 'true';
      setCardAtcState(card.querySelector('.product-card__atc'), '.product-card__atc-text', nfs || comingSoon);
      setCardAtcState(card.querySelector('.product-card__buy-btn'), '.product-card__buy-text', nfs || comingSoon);
    }
  });

  // LAC-A11Y-033: image source and alternative text always change together.
  function setImg(img, src, alt) {
    if (!img || !src) return;
    img.setAttribute('src', src);
    img.setAttribute('srcset', src);
    // The Liquid data attributes fall back to the product title. Retaining the
    // current value is a final safeguard if a malformed card omits one of them.
    if (alt) img.setAttribute('alt', alt);
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    // The hover (--second) image is loading="lazy" AND hidden until hover, so the
    // browser would not fetch the swatch until the first hover — on a cold visit
    // that first hover flashes a stale/blank frame while it downloads (a refresh
    // only "fixed" it because the file was cached by then). Flip it to eager so
    // the swatch actually downloads as part of page load, alongside the main
    // image, and is decoded before the pointer ever reaches it. fetchpriority
    // stays low so it never competes with the card's main image / LCP.
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'low');
  }

  // Default the hover (--second) image to the first variant's SWATCH image on
  // first render, so even before any swatch is picked, hovering the image shows
  // a variant swatch instead of the generic product second image. Skips cards
  // where the shopper has already selected a swatch.
  function applyDefaultHover(root) {
    var cards = (root || document).querySelectorAll('.product-card--v2');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var swatches = card.querySelectorAll('.swatch-color[href]');
      var selectedSwatch = card.querySelector('.swatch-color.is-pcard-selected');
      if (selectedSwatch) continue;
      var img2 = '';
      var img2Alt = '';
      for (var j = 0; j < swatches.length; j++) {
        var li = swatches[j].closest('li');
        var candidate = li && li.getAttribute('data-pcard-variant-image2');
        if (candidate) {
          img2 = candidate;
          img2Alt = li.getAttribute('data-pcard-variant-image2-alt') || '';
          break;
        }
      }
      // setImg flips the image to eager, so the default swatch downloads as part
      // of page load (not on first hover) — the actual fix for the cold-load flash.
      var secondImg = card.querySelector('.product-card__image--second img');
      if (img2) {
        setImg(secondImg, img2, img2Alt);
      } else if (secondImg) {
        // No swatch image for this product (no _sw / swatch file) — eager-load the
        // existing alt image so its hover is flash-free too, matching the swatch
        // cards. fetchpriority stays low so it never competes with the main image.
        secondImg.loading = 'eager';
        secondImg.setAttribute('fetchpriority', 'low');
      }
    }
  }

  document.addEventListener('keydown', function (event) {
    var swatch = event.target.closest('.product-card--v2 .swatch-color[href][role="radio"]');
    if (!swatch) return;

    var swatches = swatch.closest('.swatches').querySelectorAll('.swatch-color[href][role="radio"]');
    var currentIndex = Array.prototype.indexOf.call(swatches, swatch);
    var targetIndex = -1;

    // LAC-A11Y-014: radio-group arrow keys select the focused colour.
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = currentIndex - 1;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = currentIndex + 1;
    else if (event.key === 'Home') targetIndex = 0;
    else if (event.key === 'End') targetIndex = swatches.length - 1;
    else if (event.key === 'Enter' || event.key === ' ') targetIndex = currentIndex;
    else return;

    event.preventDefault();
    if (targetIndex < 0) targetIndex = swatches.length - 1;
    if (targetIndex >= swatches.length) targetIndex = 0;
    swatches[targetIndex].focus();
    swatches[targetIndex].click();
  });

  function initDefaultHover() {
    applyDefaultHover();
  }

  if (document.readyState !== 'loading') initDefaultHover();
  else document.addEventListener('DOMContentLoaded', initDefaultHover);
  window.addEventListener('load', initDefaultHover);
  document.addEventListener('collection:rerendered', function () {
    requestAnimationFrame(initDefaultHover);
  });
})();
